import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Gift, Loader2, Flame, Check } from "lucide-react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useWallet } from "@/contexts/WalletContext";

const getStatus = () => api.get("/rewards/status", { withCredentials: true }).then((r) => r.data);
const pad = (n) => String(n).padStart(2, "0");

const useCountdown = (iso) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!iso) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [iso]);
  if (!iso) return null;
  const ms = Math.max(new Date(iso).getTime() - now, 0);
  return `${pad(Math.floor(ms / 3600000))}:${pad(Math.floor(ms / 60000) % 60)}:${pad(Math.floor(ms / 1000) % 60)}`;
};

const StreakTrack = ({ s }) => (
  <div className="grid grid-cols-7 gap-2" data-testid="reward-streak">
    {Array.from({ length: s.streakDays }, (_, i) => {
      const day = i + 1;
      const done = day <= s.streak && !(s.canClaim && s.nextDay === 1);
      const next = s.canClaim && day === s.nextDay;
      const bonus = day === s.streakDays;
      return (
        <div key={day} data-testid={`reward-day-${day}`} data-done={done}
          className={`rounded-md border p-2 text-center transition-colors ${done ? "border-[#D4AF37]/60 bg-[#D4AF37]/10" : next ? "border-[#D4AF37] animate-pulse" : "border-[#2A2411] bg-[#050505]"}`}>
          <p className="text-[10px] text-zinc-500">Day {day}</p>
          <div className="h-5 grid place-items-center">{done ? <Check size={14} className="text-[#D4AF37]" /> : bonus ? <Flame size={14} className="text-orange-400" /> : null}</div>
          <p className={`text-xs font-mono ${bonus ? "text-orange-300" : "text-zinc-300"}`}>{s.baseAmount + (bonus ? s.bonusAmount : 0)}</p>
        </div>
      );
    })}
  </div>
);

export const DailyReward = () => {
  const qc = useQueryClient();
  const { refreshBalances } = useWallet();
  const { data: s, isError } = useQuery({ queryKey: ["rewardStatus"], queryFn: getStatus, refetchInterval: 60000 });
  const [busy, setBusy] = useState(false);
  const countdown = useCountdown(s?.nextClaimAt);

  useEffect(() => {
    if (s?.nextClaimAt && countdown === "00:00:00") qc.invalidateQueries({ queryKey: ["rewardStatus"] });
  }, [countdown, s?.nextClaimAt, qc]);

  const claim = async () => {
    setBusy(true);
    try {
      const r = await api.post("/rewards/claim", {}, { withCredentials: true }).then((x) => x.data);
      qc.setQueryData(["rewardStatus"], r);
      toast.success(r.bonus ? `Streak bonus! ${r.amount} FMI sent` : `${r.amount} FMI sent to your wallet`);
      setTimeout(() => { refreshBalances(); qc.invalidateQueries({ queryKey: ["portfolio"] }); }, 2500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Claim failed");
      qc.invalidateQueries({ queryKey: ["rewardStatus"] });
    } finally {
      setBusy(false);
    }
  };

  if (isError) return null;
  return (
    <Card data-testid="daily-reward" className="border-[#D4AF37]/30 bg-[#0A0A0A] overflow-hidden">
      <CardHeader className="pb-3 flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
        <CardTitle className="text-base font-display flex items-center gap-2"><Gift size={16} className="text-[#D4AF37]" /> Daily FMI Reward</CardTitle>
        {s && <span data-testid="reward-total" className="text-xs text-zinc-400">Total earned <span className="font-mono text-[#D4AF37]">{s.totalClaimed} FMI</span></span>}
      </CardHeader>
      <CardContent className="space-y-5">
        {!s ? <Loader2 className="animate-spin text-[#D4AF37]" /> : (
          <>
            <StreakTrack s={s} />
            <div className="flex flex-wrap items-center gap-4">
              {s.canClaim ? (
                <Button data-testid="reward-claim-btn" onClick={claim} disabled={busy} className="bg-[#D4AF37] text-black hover:bg-[#F3E5AB] font-semibold h-11 px-6">
                  {busy ? <Loader2 size={16} className="animate-spin mr-2" /> : <Gift size={16} className="mr-2" />} Claim {s.nextAmount} FMI
                </Button>
              ) : (
                <div data-testid="reward-countdown" className="font-mono text-2xl text-white">{countdown}<span className="block text-[11px] font-sans text-zinc-500">until your next claim (day {s.nextDay}: {s.nextAmount} FMI)</span></div>
              )}
              <p className="text-xs text-zinc-500 max-w-sm">Claim once every 24 hours. Claim 7 days in a row for a +{s.bonusAmount} FMI bonus on day 7. Miss a day (48h) and the streak restarts.</p>
            </div>
            {s.claims.length > 0 && (
              <ul className="space-y-1 text-xs" data-testid="reward-claims">
                {s.claims.slice(0, 5).map((c) => (
                  <li key={c.txHash} className="flex justify-between gap-3 text-zinc-400">
                    <span>{new Date(c.at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })} · day {c.day}</span>
                    <Link to={`/tx/${c.txHash}`} className="table-link font-mono">+{c.amount} FMI</Link>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
